import React, { useRef, useState, useEffect } from "react";
import VideoPlayer from "../components/VideoPlayer";
import Notification from "../components/Notification";
import { useNotification } from "../components/useNotification";
import { brandBlue, textDark, border, white, background, container, sectionPadding, heroPadding, card } from '../styles/theme';

const SsaiLive = ({ config }) => {
  const player1Ref = useRef(null);
  const player2Ref = useRef(null);
  const [selectedPair, setSelectedPair] = useState("France 24");
  const [adDuration, setAdDuration] = useState("40");
  const [token, setToken] = useState("");
  const [channelKey, setChannelKey] = useState(0); // State to force re-render of video players
  const { notification, showNotification } = useNotification();
  const configLoadedRef = useRef(false); // Track if config has been loaded initially

  useEffect(() => {
    if (configLoadedRef.current) return;
    if (config && config.liveStreams && Object.keys(config.liveStreams).length > 0 && !selectedPair) {
      setSelectedPair(Object.keys(config.liveStreams)[0]);
    }
    configLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, selectedPair]);

  const handlePlayBoth = async () => {
    try {
      // Actually play both video streams
      if (player1Ref.current) {
        player1Ref.current.play().catch(error => {
          console.error("Error playing native stream:", error);
        });
      }
      if (player2Ref.current) {
        player2Ref.current.play().catch(error => {
          console.error("Error playing SSAI stream:", error);
        });
      }
      
      showNotification('Both streams started playing', 'success');
    } catch (error) {
      console.error("Error playing streams:", error);
      showNotification('Error playing streams. Please try again.', 'error');
    }
  };

  const handleInsertAdWigoHeadend = async (channelId) => {
    const url = `https://csh02.insyscd.net/control`;
    
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          channel: channelId,
          duration: adDuration,
          token: token
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      showNotification("Ad inserted successfully!", "success");
    } catch (error) {
      console.error("Error inserting ad:", error);
      showNotification(`Error with ad insertion. Check the token or contact with the administrator. Details: ${error.message}`, "error");
    }
  };

  const handleInsertAd = async () => {
    const { channelId, type } = config.liveStreams[selectedPair].adInsertionTriggering;
    if (type === "wigo-headend") {
      await handleInsertAdWigoHeadend(channelId);
    }
  };

  // Handle channel change
  const handleChannelChange = (newChannel) => {
    console.log('Switching channel from', selectedPair, 'to', newChannel);
    
    // Update state to trigger re-render
    setSelectedPair(newChannel);
    setChannelKey(prev => prev + 1); // Force re-render of video players
    
    showNotification(`Switched to ${newChannel}`, 'success');
  };

  return (
    <div className="min-h-screen bg-white" style={{ color: textDark }}>
      {/* Hero Section with Image on Right */}
      <section className={`mx-auto ${container} px-6 ${heroPadding}`}>
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <h1 className="text-3xl md:text-5xl font-bold leading-tight" style={{ color: brandBlue }}>
              Ad insertion for live streaming
              <span className="block text-lg md:text-2xl mt-3 font-normal" style={{ color: textDark, opacity: 0.85 }}>
                Monetize your live content with dynamic ads
              </span>
            </h1>
            <p className="mt-4 max-w-2xl" style={{ opacity: 0.9 }}>
              Experience real-time ad insertion in live streams powered by AWS MediaTailor. Our SSAI solution dynamically stitches personalized ads into live content without buffering, maximizing monetization while maintaining broadcast-quality viewing experience.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              {['AWS MediaTailor', 'SCTE-35 Integration', 'Dynamic Manifest', 'Zero Buffering'].map((t,i)=> (
                <span key={i} className="rounded-full px-3 py-1 border" style={{ borderColor: brandBlue, color: brandBlue }}>
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ border: `1px solid ${border}` }}>
              <div className="aspect-video">
                <img src="/images/ssai_poster.png" alt="Video Upscaling Poster" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stream Controls and Players - 3 Columns */}
      <section id="demo-section" className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          Live Stream Comparison
        </h2>
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {/* Column 1: Stream Controls */}
          <div className="rounded-2xl p-6" style={{ background: white, border: `1px solid ${border}`, boxShadow: card }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: brandBlue }}>Stream Controls</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Channel Pair:</label>
                <select 
                  onChange={(e) => handleChannelChange(e.target.value)} 
                  value={selectedPair}
                  className="w-full p-3 border rounded-lg"
                  style={{ borderColor: border }}
                >
                  {Object.keys(config.liveStreams).map((pair) => (
                    <option key={pair} value={pair}>
                      {pair}
                    </option>
                  ))}
                </select>
              </div>

              {config.liveStreams[selectedPair].adInsertionTriggering.enabled ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Ad Duration (seconds):</label>
                    <input 
                      type="text" 
                      value={adDuration} 
                      onChange={(e) => setAdDuration(e.target.value)}
                      className="w-full p-3 border rounded-lg"
                      style={{ borderColor: border }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Auth Token:</label>
                    <input 
                      type="password" 
                      value={token} 
                      onChange={(e) => setToken(e.target.value)}
                      className="w-full p-3 border rounded-lg"
                      style={{ borderColor: border }}
                    />
                  </div>
                </>
              ) : (
                <div className="p-3 rounded-lg text-sm" style={{ background: background, border: `1px dashed ${border}` }}>
                  Ad breaks based on SCTE markers from source stream
                </div>
              )}
              
              <div className="flex gap-3">
                <button 
                  className="flex-1 px-4 py-2 rounded-lg font-medium" 
                  style={{ background: brandBlue, color: white }}
                  onClick={handlePlayBoth}
                >
                  Play Both
                </button>
                {config.liveStreams[selectedPair].adInsertionTriggering.enabled && (
                  <button 
                    className="flex-1 px-4 py-2 rounded-lg font-medium border" 
                    style={{ borderColor: brandBlue, color: brandBlue }}
                    onClick={handleInsertAd}
                  >
                    Insert Ad
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Column 2: SSAI Stream Player */}
          <div className="rounded-2xl overflow-hidden shadow-lg h-fit" style={{ border: `1px solid ${border}` }}>
            <div className="p-4" style={{ background: background, borderBottom: `1px solid ${border}` }}>
              <h3 className="font-semibold" style={{ color: brandBlue }}>SSAI Stream</h3>
            </div>
            <VideoPlayer
              key={`ssai-${selectedPair}-${channelKey}`} // Include channel name in key for complete re-render
              label="SSAI Stream"
              src={config.liveStreams[selectedPair].ssai}
              playerRef={player2Ref}
            />
          </div>
          
          {/* Column 3: Native Stream Player */}
          <div className="rounded-2xl overflow-hidden shadow-lg h-fit" style={{ border: `1px solid ${border}` }}>
            <div className="p-4" style={{ background: background, borderBottom: `1px solid ${border}` }}>
              <h3 className="font-semibold" style={{ color: brandBlue }}>Native Stream</h3>
            </div>
            <VideoPlayer
              key={`native-${selectedPair}-${channelKey}`} // Include channel name in key for complete re-render
              label="Native Stream"
              src={config.liveStreams[selectedPair].native}
              playerRef={player1Ref}
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          SSAI: How it works
        </h2>
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <h3 className="text-lg font-semibold mb-4" style={{ color: brandBlue }}>Real-Time Ad Insertion Process</h3>
            <ul className="space-y-3 text-sm" style={{ color: textDark, opacity: 0.9 }}>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>SCTE-35 Markers:</strong> Live stream contains ad break triggers that signal when to insert ads</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Dynamic Manifest:</strong> AWS MediaTailor manipulates HLS manifests in real-time to stitch ad content</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Personalized Ads:</strong> Ad Decision Server returns targeted content based on viewer demographics</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Seamless Delivery:</strong> Single stream with ads and content delivered via CloudFront CDN</span>
              </li>
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-2xl p-5" style={{ background: white, border: `1px solid ${border}`, boxShadow: card, color: textDark }}>
              <h3 className="font-semibold mb-3" style={{ color: brandBlue }}>Key Benefits</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Zero buffering during ad transitions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Device-agnostic ad delivery</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Real-time targeting and personalization</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Broadcast-quality viewing experience</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          Technical Features & Capabilities
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "AWS MediaTailor Integration",
              badges: ["HLS/DASH Support", "DRM Compatible", "CloudFront CDN"],
              desc: "Native AWS service integration for reliable, scalable ad insertion"
            },
            {
              title: "Live Stream Processing",
              badges: ["SCTE-35 Markers", "Real-time Manifest", "Dynamic Stitching"],
              desc: "Process live streams with ad break triggers for seamless monetization"
            },
            {
              title: "Ad Decision Server",
              badges: ["VAST Support", "Targeting Logic", "Beacon Tracking"],
              desc: "Flexible integration with existing ad servers or custom solutions"
            }
          ].map((feature, i) => (
            <article key={i} className="group rounded-2xl overflow-hidden transition" style={{ background: white, border: `1px solid ${border}`, boxShadow: card }}>
              <div className="p-5" style={{ color: textDark }}>
                <h3 className="font-medium mb-3" style={{ color: brandBlue }}>{feature.title}</h3>
                <p className="text-sm mb-3" style={{ opacity: 0.85 }}>{feature.desc}</p>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {feature.badges.map((badge, j) => (
                    <span key={j} className="rounded-full px-2.5 py-1 border" style={{ borderColor: brandBlue, color: brandBlue }}>{badge}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6" style={{ background: brandBlue }}>
        <div className={`mx-auto ${container} text-center`}>
          <h2 className="text-3xl font-bold mb-4 text-white">
            Ready to Monetize Your Live Streams?
          </h2>
          <p className="text-xl mb-8 text-white opacity-90">
            Our SSAI solution can be quickly deployed and customized for your specific needs. Maximize your revenue with real-time ad insertion.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="https://www.bigbluemarble.com/en/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
              style={{ background: white, color: brandBlue, textDecoration: 'none' }}
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <Notification notification={notification} />
    </div>
  );
};

export default SsaiLive;